import React, { useEffect, useMemo } from "react";

import { useMutation, useQuery } from "@apollo/client";
import PhotoIcon from "@mui/icons-material/Photo";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DirectUpload } from "@rails/activestorage";
import { format } from "date-fns";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Controller, DefaultValues } from "react-hook-form";
import { NavLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import * as yup from "yup";

import DropzoneInput from "@/components/DropzoneInput";
import MobileInput from "@/components/MobileInput";
import { graphql } from "@/gql";
import { Gender, UpdateMyProfileInput } from "@/gql/graphql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import { muiField, muiFieldError } from "@/utils/hookForm";
import { humanize } from "@/utils/string";

import { personalInfoQuery } from "./PersonalInfo";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.white,
    padding: theme.spacing(4),
    height: "100%",
  },
  headerText: {
    fontSize: 28,
    fontWeight: "bold",
    lineHeight: 1.25,
    color: colors.dark,
  },
  subHeaderText: {
    fontSize: 18,
    fontWeight: 600,
    margin: theme.spacing(4, 0),
    color: colors.dark,
  },
  profileInfo: {
    display: "flex",
    flexDirection: "column",
  },
  profileInfoContainer: {
    display: "flex",
    backgroundColor: colors.white,
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#EDF0F5",
  },
  table: {
    tableLayout: "fixed",
  },
  label: {
    marginBottom: 12,
    fontWeight: 600,
    fontSize: 14,
    color: colors.gray,
    display: "flex",
    alignItems: "center",
  },
  photoDrop: {
    flex: 1,
    border: "1px dotted #C8CED8",
    aspectRatio: "1",
    // height: "100%",
    padding: 10,
  },
  photoDropInner: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
    backgroundColor: colors.light,
  },
  body1: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1.25,
    color: colors.dark,
  },
  body2: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.25,
    color: colors.gray,
  },
  preview: {
    height: 120,
    width: 120,
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "row",
    marginTop: "36px",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: "32px",
    paddingInline: "24px",
  },
  cancelButton: {
    marginRight: theme.spacing(4),
    paddingInline: "24px",
  },
  loadingView: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const validationSchema = yup
  .object()
  .shape({
    firstName: yup.string().label("First name").required(),
    middleName: yup.string().label("Middle name").nullable(),
    lastName: yup.string().label("Last name").nullable(),
    gender: yup
      .string()
      .label("Gender")
      .required()
      .oneOf(Object.values(Gender)),
    dateOfBirth: yup.date().nullable(),
    mobileNum: yup
      .string()
      .label("Mobile number")
      .required()
      .nullable()
      .test("phoneNumber", "${path} is not a valid phone number", (value) => {
        if (value == null) return true;
        return isValidPhoneNumber(value);
      }),
    avatar: yup.mixed<File>().nullable(),
    avatarUrl: yup.string().nullable(),
    country: yup.string().label("Country").required().nullable(),
    occupation: yup.string().label("Occupation").required().nullable(),
  })
  .defined();

type FormValues = yup.InferType<typeof validationSchema>;

const RequiredIndicator = (): JSX.Element => (
  <Typography color="error">*</Typography>
);

const updateMyProfileMutation = graphql(`
  mutation UpdateMyProfile($input: UpdateMyProfileInput!) {
    updateMyProfile(input: $input) {
      coach {
        id
        profile {
          id
        }
      }
      errors {
        fullMessage
      }
    }
  }
`);

const countriesQuery = graphql(`
  query Countries {
    countries {
      alpha2
      isoLongName
      isoShortName
    }
  }
`);

const EditPersonalInfo: React.FC = () => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const { data: countriesData, loading: countriesLoading } =
    useQuery(countriesQuery);
  const { data, loading } = useQuery(personalInfoQuery);
  const [updateProfile] = useMutation(updateMyProfileMutation);
  const personalInfo = data?.coachViewer;
  const profileImg = personalInfo?.profile?.avatarUrl;
  const avatarSignedId = personalInfo?.profile?.avatar.signedId;
  const firstName = personalInfo?.profile?.firstName;
  const lastName = personalInfo?.profile?.lastName;
  const middleName = personalInfo?.profile?.middleName;
  const dateOfBirth = personalInfo?.profile?.dateOfBirth;
  const gender = personalInfo?.profile?.gender ?? ("" as unknown as Gender);
  const mobileNum = personalInfo?.profile?.mobileNumber;
  const country = personalInfo?.profile?.country ?? "PH";
  const occupation = personalInfo?.profile?.occupation;
  const countries = countriesData?.countries ?? [];
  const initialValues: DefaultValues<FormValues> = {
    firstName,
    lastName,
    middleName,
    gender,
    mobileNum,
    dateOfBirth: dateOfBirth ? new Date(dateOfBirth) : null,
    avatar: null,
    avatarUrl: avatarSignedId,
    country,
    occupation,
  };
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, dirtyFields },
  } = useFormWithYup<FormValues>({
    defaultValues: initialValues,
    validationSchema,
    reValidateMode: "onBlur",
  });
  useEffect(() => {
    if (dirtyFields.avatar) {
      uploadAvatar(watch("avatar"));
    }
  }, [dirtyFields.avatar]);

  function uploadAvatar(avatar: File | null) {
    if (avatar !== null) {
      const url = `${import.meta.env.VITE_API_URL}/api/direct_uploads`;
      const upload = new DirectUpload(avatar, url);
      upload.create((error, blob) => {
        if (error) {
          snackbar.open(displayError(error, "Error: Cannot upload photo."));
        }
        setValue("avatarUrl", blob.signed_id);
      });
    }
  }

  const onSubmit = async (values: FormValues) => {
    const date = values.dateOfBirth ?? new Date();

    const updateProfileInput: UpdateMyProfileInput = {
      patch: {
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName,
        gender: values.gender,
        mobileNumber: values.mobileNum,
        avatar: values.avatarUrl,
        dateOfBirth: format(new Date(date), "yyyy-MM-dd"),
        country: values.country ?? ``,
        occupation: values.occupation ?? "",
      },
    };
    try {
      await updateProfile({
        variables: { input: updateProfileInput },
        refetchQueries: [personalInfoQuery],
      });
      snackbar.open("Your changes has been saved.");
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "An error occured while updating your profile. Please try again later."
        )
      );
    }
  };

  const newProfileImgPreview = useMemo(() => {
    const file = watch("avatar");
    return file && URL.createObjectURL(file);
  }, [watch("avatar")]);

  // Reset the form when existing values are finished loading. Equivalent to
  // Formik's reinitialize prop.
  useEffect(() => {
    if (!loading) reset(initialValues);
  }, [loading]);

  if (loading) {
    return (
      <div className={classes.loadingView}>
        <Typography variant="h6" color="textPrimary">
          Loading...
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.headerText}>Edit Profile</Typography>
      <Typography className={classes.subHeaderText}>
        Personal details
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="avatar"
              control={control}
              render={({ field }) => (
                <DropzoneInput
                  {...muiField(field)}
                  className={classes.photoDrop}
                  accept="image/*"
                  multiple={false}
                  noClick
                >
                  {(open) => (
                    <div className={classes.photoDropInner}>
                      <Box mb={3}>
                        <PhotoIcon htmlColor={colors.gray} />
                      </Box>
                      <Typography className={classes.body2}>
                        Drag and drop your photo here
                      </Typography>
                      <Box my={3} mb={3}>
                        <Typography className={classes.body1}>or</Typography>
                      </Box>
                      <Button onClick={open}>Browse</Button>
                    </div>
                  )}
                </DropzoneInput>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Box>
              <div className={classes.label}>Preview</div>
              <Box display="flex" justifyContent="center">
                <Avatar
                  className={classes.preview}
                  alt="preview"
                  src={newProfileImgPreview ?? profileImg ?? ""}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormLabel>
              <div className={classes.label}>
                First name <RequiredIndicator />
              </div>
              <Controller
                name="firstName"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <TextField
                    {...muiField(field)}
                    {...muiFieldError(errors.firstName)}
                    fullWidth
                  />
                )}
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormLabel>
              <div className={classes.label}>
                Last name <RequiredIndicator />
              </div>
              <Controller
                name="lastName"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <TextField
                    {...muiField(field)}
                    {...muiFieldError(errors.lastName)}
                    fullWidth
                  />
                )}
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormLabel>
              <div className={classes.label}>Middle name</div>
              <Controller
                name="middleName"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <TextField
                    {...muiField(field)}
                    {...muiFieldError(errors.middleName)}
                    fullWidth
                  />
                )}
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormLabel>
              <div className={classes.label}>Date of birth</div>
              <Controller
                control={control}
                name="dateOfBirth"
                render={({
                  field: { ref, name, onChange, ...field },
                  fieldState,
                }) => (
                  <DatePicker
                    {...field}
                    inputRef={ref}
                    onChange={(value) => onChange(value)}
                    slotProps={{
                      textField: {
                        name,
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                )}
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormLabel>
              <div className={classes.label}>
                Gender <RequiredIndicator />
              </div>
              <Controller
                name="gender"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <TextField
                    {...muiField(field)}
                    {...muiFieldError(errors.gender)}
                    select
                    fullWidth
                  >
                    <MenuItem value={Gender.Male}>
                      {humanize(Gender.Male)}
                    </MenuItem>
                    <MenuItem value={Gender.Female}>
                      {humanize(Gender.Female)}
                    </MenuItem>
                  </TextField>
                )}
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormLabel>
              <div className={classes.label}>
                Mobile number <RequiredIndicator />
              </div>
              <Controller
                name="mobileNum"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <MobileInput
                    {...muiField(field)}
                    {...muiFieldError(errors.mobileNum)}
                  />
                )}
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormLabel>
              <div className={classes.label}>
                Country <RequiredIndicator />
              </div>
              <Controller
                name="country"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <TextField
                    {...muiField(field)}
                    {...muiFieldError(errors.country)}
                    select
                    disabled={countriesLoading}
                  >
                    <MenuItem disabled value="">
                      Select country
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem
                        key={country.alpha2}
                        value={country.isoShortName}
                      >
                        {country.isoShortName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormLabel>
              <div className={classes.label}>
                Occupation <RequiredIndicator />
              </div>
              <Controller
                name="occupation"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <TextField
                    {...muiField(field)}
                    {...muiFieldError(errors.occupation)}
                  />
                )}
              />
            </FormLabel>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center">
              <Button
                variant="outlined"
                className={classes.cancelButton}
                component={NavLink}
                to="/profile"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className={classes.saveButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} /> : "Save"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default EditPersonalInfo;
